import {Component, OnInit} from '@angular/core';
import {
  AgGridComponentFrameworkComponent
} from "../../../../../shared/components/ag-grid/ag-grid-component-framework/ag-grid-component-framework.component";
import {CellSourceComponent} from "../../../../../shared/components/cell-source/cell-source.component";
import {Serializer, Serializers} from "../../../../../shared/interfaces/serializer";
import {DataSetMetric, DataSetMetricsGroup} from '../../../../../shared/interfaces/metrics';
import {CellSelectComponent} from "../../../../../shared/components/cell-select/cell-select.component";
import {TranslateService} from "@ngx-translate/core";
import {SelectOption} from "../../../../../shared/interfaces/form";
import {Visibility} from "../../../../../shared/interfaces/settings";
import {AppService} from "../../../../../shared/services/app.service";
import {SettingsService} from "../../../../../shared/services/settings.service";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../../shared/store/store";
import {Observable, withLatestFrom} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {
  selectDataSetMetricsLoading,
  selectDataSetMetricSourceComponentObjects
} from '../../../../../shared/store/dataSetMetrics/dataSetMetrics.selectors';
import {
  selectDataSetMetricsGroupComponentObjects,
  selectDataSetMetricsGroupsLoaded
} from '../../../../../shared/store/dataSetMetricsGroups/dataSetMetricsGroups.selectors';
import {ColDef} from '@ag-grid-community/core';
import {updateDataSetMetric} from '../../../../../shared/store/dataSetMetrics/dataSetMetrics.actions';
import {DatasetMetricsGroupComponentObjects} from '../../../../../shared/classes/dataset-metrics-group-component-object';
import {MetricComponentObject, MetricComponentObjects} from '../../../../../shared/classes/metric-component-object';

@Component({
  selector: 'app-metrics-source-table',
  templateUrl: './metrics-source-table.component.html',
  styleUrls: ['./metrics-source-table.component.scss']
})
export class MetricsSourceTableComponent implements OnInit {
  public readonly defaultColDef: any = {
    resizable: true,
    suppressNavigable: true,
    suppressCellSelection: true,
    menuTabs: ['filterMenuTab'],
    filterParams: {
      buttons: ['reset']
    }
  };

  public readonly columnDefs$: Observable<Array<ColDef>> = this._store.select(selectDataSetMetricsGroupsLoaded).pipe(
    filter((loaded: boolean): boolean => loaded),
    withLatestFrom(this._store.select(selectDataSetMetricsGroupComponentObjects)),
    map(([_, groups]: [boolean, DatasetMetricsGroupComponentObjects]): Array<ColDef> => {
      return [
        {
          minWidth: 200,
          sortable: true,
          filter: 'agTextColumnFilter',
          lockPosition: true,
          headerValueGetter: _ => {
            return this._translateS.instant('settings.metric');
          },
          valueGetter: params => params.data.payload.attributes.name,
          filterValueGetter: params => params.data.payload.attributes.name,
          cellRendererFramework: AgGridComponentFrameworkComponent,
          cellRendererParams: _ => {
            return {
              component:  CellSourceComponent,
              componentParams: {
                sourceGetter: (row: Serializer<DataSetMetric>) => row,
                showCategory: false
              }
            };
          }
        },
        {
          minWidth: 200,
          sortable: true,
          filter: 'agTextColumnFilter',
          lockPosition: true,
          headerValueGetter: _ => {
            return this._translateS.instant('settings.source');
          },
          valueGetter: params => params.data.payload.relationships.data_source.data.attributes.name,
          filterValueGetter: params => params.data.payload.relationships.data_source.data.attributes.name,
          cellRendererFramework: AgGridComponentFrameworkComponent,
          cellRendererParams: _ => {
            return {
              component:  CellSourceComponent,
              componentParams: {
                sourceGetter: (row: MetricComponentObject) => ({
                  name: row.payload.relationships.data_source.data.attributes.name,
                  icon: row.payload.relationships.data_source.data.attributes.icon_small
                })
              }
            };
          }
        },
        {
          minWidth: 200,
          sortable: true,
          filter: true,
          lockPosition: true,
          tooltipComponent: 'customTooltip',
          headerTooltip: this._translateS.instant('settings.visibility_tooltip'),
          valueGetter: params => params.data.payload.attributes.visibility,
          headerValueGetter: _ => {
            return this._translateS.instant('settings.visibility');
          },
          cellRendererFramework: AgGridComponentFrameworkComponent,
          cellRendererParams: _ => {
            return {
              component:  CellSelectComponent,
              componentParams: this._getVisibilityParams()
            };
          },
          filterValueGetter: params => this._translateS.instant(`settings.${params.data.attributes.visibility}`)
        },
        {
          minWidth: 200,
          sortable: true,
          filter: 'agTextColumnFilter',
          lockPosition: true,
          headerValueGetter: _ => this._translateS.instant('settings.category'),
          filterValueGetter: params => params.data.payload.relationships.data_set_metrics_group.data?.attributes.name || this._translateS.instant('settings.source_menu_only'),
          valueGetter: params => params.data.payload.relationships.data_set_metrics_group.data?.attributes.name,
          cellRendererFramework: AgGridComponentFrameworkComponent,
          cellRendererParams: _ => {
            return {
              component:  CellSelectComponent,
              componentParams: this._getCategoryParams(groups)
            };
          }
        }
      ];
    })
  );

  public readonly metrics$: Observable<MetricComponentObjects> = this._store.select(selectDataSetMetricSourceComponentObjects);
  public readonly loading$: Observable<boolean> = this._store.select(selectDataSetMetricsLoading);

  constructor(
    private readonly _translateS: TranslateService,
    private readonly _appS: AppService,
    private readonly _settingsS: SettingsService,
    private readonly _store: Store<AppState>
  ) { }

  ngOnInit(): void {}

  private _getVisibilityParams(): any {
    return {
      options:            this._settingsS.visibilities,
      disabled:           this._appS.isNotDatasetAdmin,
      defaultValueGetter: (metric: MetricComponentObject) => (metric.payload.attributes as DataSetMetric).visibility,
      textGetter:         (visibility: SelectOption<Visibility, string>) => visibility.text,
      valueGetter:        (visibility: SelectOption<Visibility, string>) => visibility.key,
      onValueChanges:     (row: any, value: any) => this._updateMetric(row, { visibility: value })
    };
  }

  private _getCategoryParams(metricGroups: DatasetMetricsGroupComponentObjects): any {
    return {
      options:            [{id: '0', attributes: {name: 'settings.source_menu_only'}}, ...metricGroups.map((group: MetricComponentObject): Serializer<DataSetMetricsGroup> => group.payload as Serializer<DataSetMetricsGroup>)],
      disabled:           this._appS.isNotDatasetAdmin,
      defaultValueGetter: (category: MetricComponentObject) => {
        return category.payload.relationships.data_set_metrics_group.data?.id || '0';
      },
      textGetter:         (category: Serializer<DataSetMetricsGroup>) => category.attributes.name,
      valueGetter:        (category: Serializer<DataSetMetricsGroup>) => category.id,
      onValueChanges:     (row: any, value: number) => this._updateMetric(row, { data_set_metrics_group_id: value })
    };
  }

  private _updateMetric(metric: MetricComponentObject, option: Partial<DataSetMetric>): void {
    this._store.dispatch(updateDataSetMetric({
      metric: metric.payload as Serializer<DataSetMetric>,
      update: option
    }));
  }

}
